import { Table, Badge, Pagination, Button, Checkbox } from "flowbite-react";
import { HiPencil, HiTrash, HiEye } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { KbIssue, DataGridPagination, VectorizeStatusType } from "@/sharedTypes";

const vectorizeBadgeColor: Record<VectorizeStatusType, string> = {
  not_vectorized: "gray",
  pending: "warning",
  processing: "purple",
  vectorized: "success",
  failed: "failure",
};

interface KbIssueTableProps {
  data: KbIssue[];
  pagination: DataGridPagination;
  onPageChange: (page: number) => void;
  onEdit: (issue: KbIssue) => void;
  onDelete: (issue: KbIssue) => void;
  onView: (issue: KbIssue) => void;
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
}

export default function KbIssueTable({
  data,
  pagination,
  onPageChange,
  onEdit,
  onDelete,
  onView,
  selectedIds,
  onSelectionChange,
}: KbIssueTableProps) {
  const { t } = useTranslation(["knowledgeBase", "common"]);

  // Selectable = not yet approved, OR approved but not yet successfully vectorized.
  const selectableData = data.filter(
    (issue) => !issue.is_ai_approved || issue.vectorize_status !== "vectorized"
  );
  const allSelectableSelected =
    selectableData.length > 0 &&
    selectableData.every((issue) => selectedIds.includes(issue.id!));

  const handleSelectAll = () => {
    if (allSelectableSelected) {
      onSelectionChange(selectedIds.filter((id) => !selectableData.some((issue) => issue.id === id)));
    } else {
      const newIds = selectableData.map((issue) => issue.id!).filter((id) => !selectedIds.includes(id));
      onSelectionChange([...selectedIds, ...newIds]);
    }
  };

  const handleSelectOne = (issueId: number) => {
    if (selectedIds.includes(issueId)) {
      onSelectionChange(selectedIds.filter((id) => id !== issueId));
    } else {
      onSelectionChange([...selectedIds, issueId]);
    }
  };

  return (
    <div className="overflow-x-auto rounded-lg shadow">
      <Table hoverable>
        <Table.Head>
          <Table.HeadCell className="w-10">
            {selectableData.length > 0 && (
              <Checkbox checked={allSelectableSelected} onChange={handleSelectAll} />
            )}
          </Table.HeadCell>
          <Table.HeadCell className="w-16">{t("knowledgeBase:id")}</Table.HeadCell>
          <Table.HeadCell>{t("knowledgeBase:issueText")}</Table.HeadCell>
          <Table.HeadCell>{t("knowledgeBase:category")}</Table.HeadCell>
          <Table.HeadCell>{t("knowledgeBase:subcategory")}</Table.HeadCell>
          <Table.HeadCell>{t("knowledgeBase:aiApproved")}</Table.HeadCell>
          <Table.HeadCell>{t("knowledgeBase:vectorized")}</Table.HeadCell>
          <Table.HeadCell>{t("knowledgeBase:actions")}</Table.HeadCell>
        </Table.Head>
        <Table.Body className="divide-y">
          {data.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={8} className="text-center py-8 text-gray-500">
                {t("knowledgeBase:noIssues")}
              </Table.Cell>
            </Table.Row>
          ) : (
            data.map((issue) => (
              <Table.Row
                key={issue.id}
                className="bg-white dark:border-gray-700 dark:bg-gray-800"
              >
                <Table.Cell className="w-10">
                  {(!issue.is_ai_approved || issue.vectorize_status !== "vectorized") && (
                    <Checkbox
                      checked={selectedIds.includes(issue.id!)}
                      onChange={() => handleSelectOne(issue.id!)}
                    />
                  )}
                </Table.Cell>
                <Table.Cell className="w-16 text-gray-500 text-sm font-mono">
                  {issue.id}
                </Table.Cell>
                <Table.Cell className="max-w-xs truncate font-medium text-gray-900 dark:text-white">
                  {issue.kb_issue_text}
                </Table.Cell>
                <Table.Cell>
                  {issue.category?.category_name || "-"}
                </Table.Cell>
                <Table.Cell>
                  {issue.subcategory?.subcategory_name || "-"}
                </Table.Cell>
                <Table.Cell>
                  <Badge color={issue.is_ai_approved ? "success" : "gray"} size="sm">
                    {issue.is_ai_approved ? t("knowledgeBase:yes") : t("knowledgeBase:no")}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Badge color={vectorizeBadgeColor[issue.vectorize_status || "not_vectorized"]} size="sm">
                    {t(`knowledgeBase:vectorizeStatus.${issue.vectorize_status || "not_vectorized"}`)}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <div className="flex gap-2">
                    <Button size="xs" color="light" onClick={() => onView(issue)}>
                      <HiEye className="h-4 w-4" />
                    </Button>
                    <Button size="xs" color="light" onClick={() => onEdit(issue)}>
                      <HiPencil className="h-4 w-4" />
                    </Button>
                    <Button size="xs" color="failure" onClick={() => onDelete(issue)}>
                      <HiTrash className="h-4 w-4" />
                    </Button>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))
          )}
        </Table.Body>
      </Table>
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-center py-4">
          <Pagination
            currentPage={pagination.pageIndex}
            totalPages={pagination.totalPages}
            onPageChange={onPageChange}
            showIcons
          />
        </div>
      )}
    </div>
  );
}
